import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { useImpersonationStore } from '@/store/impersonationStore';
import { useAuthStore } from '@/store/authStore';
import { sanitizeFilename } from './company-assets';
import { getProductMode } from '@/lib/product-mode';
import type {
  Company,
  CompanyMember,
  CreateCompanyRequest,
  UpdateCompanyRequest,
} from '@shared/types';

// =====================================================
// Query: Get Current User's Company
// =====================================================

const COMPANY_CACHE_KEY = 'cachedCompany';

/**
 * Get cached company data from localStorage
 * Used to provide instant initial data on page load
 */
function getCachedCompany(): Company | null {
  try {
    const cached = localStorage.getItem(COMPANY_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      // Validate cached user ID matches current user to avoid stale data
      const cachedUserId = localStorage.getItem('cachedUserId');
      if (cachedUserId && parsed.cachedForUserId === cachedUserId) {
        return parsed.company;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

/**
 * Cache company data to localStorage for faster subsequent loads
 */
function cacheCompany(company: Company | null, userId: string): void {
  try {
    if (company) {
      localStorage.setItem(COMPANY_CACHE_KEY, JSON.stringify({
        company,
        cachedForUserId: userId,
        cachedAt: Date.now(),
      }));
    } else {
      localStorage.removeItem(COMPANY_CACHE_KEY);
    }
  } catch {
    // Ignore storage errors
  }
}

/**
 * Get the current user's primary company
 * Returns the first company the user belongs to
 * Respects impersonation: if impersonating, returns impersonated user's company
 */
export function useCurrentCompany() {
  // Get impersonation session from store
  const impersonationSession = useImpersonationStore((state) => state.session);

  // Auth-readiness gate: don't fire the company RPC until the session is fully
  // settled (setUser has run). Firing earlier races token propagation and the
  // RPC 400s, which used to flash the "Unable to Load Workspace" screen on login.
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const authLoading = useAuthStore((state) => state.isLoading);

  return useQuery({
    // Include impersonated user ID in query key to refetch when impersonation changes
    queryKey: ['company', 'current', impersonationSession?.impersonated_user_id],
    queryFn: async (): Promise<Company | null> => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('Not authenticated');
      }

      // Determine effective user ID (impersonated user or actual user)
      const effectiveUserId = impersonationSession?.impersonated_user_id || user.id;

      console.log('[useCurrentCompany] Fetching company for user:', {
        authUserId: user.id,
        impersonatedUserId: impersonationSession?.impersonated_user_id,
        effectiveUserId,
        isImpersonating: !!impersonationSession?.impersonated_user_id,
      });

      // Use SECURITY DEFINER function to bypass RLS
      // This is critical during impersonation where auth.uid() != effectiveUserId
      const { data, error } = await supabase.rpc('get_user_company', {
        p_user_id: effectiveUserId,
      });

      if (error) {
        console.error('[useCurrentCompany] Error fetching current company:', error);
        throw error;
      }

      console.log('[useCurrentCompany] Query result:', {
        hasCompany: Array.isArray(data) && data.length > 0,
        companyData: data,
      });

      // RPC RETURNS TABLE returns array - get first item or null
      const companyData = Array.isArray(data) && data.length > 0 ? data[0] : null;

      if (!companyData) {
        console.warn('[useCurrentCompany] No company found for user:', effectiveUserId);
        cacheCompany(null, effectiveUserId);
        return null;
      }

      const company: Company = {
        id: companyData.company_id,
        name: companyData.company_name,
        logoUrl: companyData.logo_url,
        contactEmail: companyData.contact_email,
        contactPhone: companyData.contact_phone,
        brandColor: companyData.brand_color,
        primaryColor: companyData.primary_color,
        defaultCurrency: companyData.default_currency || 'USD',
        productMode: companyData.product_mode ?? 'full',
        address: companyData.address,
        city: companyData.city,
        postalCode: companyData.postal_code,
        country: companyData.country,
        settings: companyData.settings || {},
        createdAt: companyData.created_at,
        updatedAt: companyData.updated_at,
        deletedAt: null, // Function filters out deleted companies
      };

      // Cache for faster subsequent loads
      cacheCompany(company, effectiveUserId);

      return company;
    },
    // Only run once auth has settled to avoid the login token-propagation race.
    enabled: isAuthenticated && !authLoading,
    // Smooth transient 400s while the freshly-issued token propagates.
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
    staleTime: 1000 * 60 * 5, // 5 minutes
    placeholderData: keepPreviousData, // Prevent logo flickering during refetches
    // Use cached data as initial data to skip loading state on subsequent visits
    initialData: () => getCachedCompany() ?? undefined,
    // Mark initial data as potentially stale so it refetches in background
    initialDataUpdatedAt: 0,
  });
}

// =====================================================
// Query: Get Current User's Company Membership
// =====================================================

/**
 * Get the current user's company membership with role
 * Respects impersonation: if impersonating, returns impersonated user's membership
 */
export function useCurrentCompanyMember() {
  // Get impersonation session from store
  const impersonationSession = useImpersonationStore((state) => state.session);

  return useQuery({
    // Include impersonated user ID in query key to refetch when impersonation changes
    queryKey: ['company-member', 'current', impersonationSession?.impersonated_user_id],
    queryFn: async (): Promise<CompanyMember | null> => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('Not authenticated');
      }

      // Determine effective user ID (impersonated user or actual user)
      const effectiveUserId = impersonationSession?.impersonated_user_id || user.id;

      // Use SECURITY DEFINER RPC instead of querying company_members directly.
      // Direct query runs under the caller JWT, so during superadmin impersonation
      // RLS filters out the impersonated user row and .single() returns 406.
      // Mirrors the get_user_company pattern from migration 20251016000002.
      const { data, error } = await supabase
        .rpc('get_user_company_member', { p_user_id: effectiveUserId });

      if (error) {
        console.error('Error fetching company membership:', error);
        throw error;
      }

      const row = Array.isArray(data) ? data[0] : data;
      if (!row) {
        return null;
      }

      return {
        id: row.id,
        companyId: row.company_id,
        userId: row.user_id,
        role: row.role,
        invitedBy: row.invited_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        deletedAt: row.deleted_at,
      };
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// =====================================================
// Query: Get Company by ID
// =====================================================

export function useCompany(companyId: string | undefined) {
  return useQuery({
    queryKey: ['company', companyId],
    queryFn: async (): Promise<Company> => {
      if (!companyId) {
        throw new Error('Company ID is required');
      }

      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .is('deleted_at', null)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Company not found');

      return {
        id: data.id,
        name: data.name,
        logoUrl: data.logo_url,
        contactEmail: data.contact_email,
        contactPhone: data.contact_phone,
        brandColor: data.brand_color,
        primaryColor: data.primary_color,
        defaultCurrency: data.default_currency || 'USD',
        productMode: (data.product_mode ?? 'full') as 'full' | 'feedback',
        website: data.website,
        address: data.address,
        city: data.city,
        postalCode: data.postal_code,
        country: data.country,
        settings: data.settings || {},
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        deletedAt: data.deleted_at,
      };
    },
    enabled: !!companyId,
  });
}

// =====================================================
// Mutation: Create Company
// =====================================================

export function useCreateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateCompanyRequest): Promise<Company> => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('Not authenticated');
      }

      // Create company - tag product_mode by signup-origin hostname (D-07)
      // feedback-os.com signups become 'feedback'; all other origins default to 'full'
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .insert({
          name: request.name,
          logo_url: request.logoUrl ?? null,
          contact_email: request.contactEmail ?? null,
          contact_phone: request.contactPhone ?? null,
          brand_color: request.brandColor ?? '#3B82F6',
          primary_color: request.primaryColor ?? null,
          address: request.address ?? null,
          city: request.city ?? null,
          postal_code: request.postalCode ?? null,
          country: request.country ?? null,
          settings: {},
          product_mode: getProductMode(),
        })
        .select()
        .single();

      if (companyError) throw companyError;
      if (!company) throw new Error('Failed to create company');

      // Add user as owner
      const { error: memberError } = await supabase.from('company_members').insert({
        company_id: company.id,
        user_id: user.id,
        role: 'owner',
      });

      if (memberError) throw memberError;

      return {
        id: company.id,
        name: company.name,
        logoUrl: company.logo_url,
        contactEmail: company.contact_email,
        contactPhone: company.contact_phone,
        brandColor: company.brand_color,
        primaryColor: company.primary_color,
        defaultCurrency: company.default_currency || 'USD',
        productMode: (company.product_mode ?? 'full') as 'full' | 'feedback',
        address: company.address,
        city: company.city,
        postalCode: company.postal_code,
        country: company.country,
        settings: company.settings || {},
        createdAt: company.created_at,
        updatedAt: company.updated_at,
        deletedAt: company.deleted_at,
      };
    },
    onSuccess: () => {
      // Invalidate company queries
      queryClient.invalidateQueries({ queryKey: ['company'] });
    },
  });
}

// =====================================================
// Mutation: Update Company
// =====================================================

export function useUpdateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      updates,
    }: {
      companyId: string;
      updates: UpdateCompanyRequest;
    }): Promise<Company> => {
      const updatePayload = {
        ...(updates.name !== undefined && { name: updates.name }),
        ...(updates.logoUrl !== undefined && { logo_url: updates.logoUrl }),
        ...(updates.contactEmail !== undefined && { contact_email: updates.contactEmail }),
        ...(updates.contactPhone !== undefined && { contact_phone: updates.contactPhone }),
        ...(updates.website !== undefined && { website: updates.website }),
        ...(updates.brandColor !== undefined && { brand_color: updates.brandColor }),
        ...(updates.primaryColor !== undefined && { primary_color: updates.primaryColor }),
        ...(updates.defaultCurrency !== undefined && { default_currency: updates.defaultCurrency }),
        ...(updates.address !== undefined && { address: updates.address }),
        ...(updates.city !== undefined && { city: updates.city }),
        ...(updates.postalCode !== undefined && { postal_code: updates.postalCode }),
        ...(updates.country !== undefined && { country: updates.country }),
        ...(updates.settings !== undefined && { settings: updates.settings }),
      };

      console.log('Updating company with payload:', updatePayload);

      const { data, error } = await supabase
        .from('companies')
        .update(updatePayload)
        .eq('id', companyId)
        .select()
        .single();

      console.log('Update result:', { data, error });

      if (error) {
        console.error('Update error details:', error);
        throw error;
      }
      if (!data) throw new Error('Failed to update company');

      return {
        id: data.id,
        name: data.name,
        logoUrl: data.logo_url,
        contactEmail: data.contact_email,
        contactPhone: data.contact_phone,
        brandColor: data.brand_color,
        primaryColor: data.primary_color,
        defaultCurrency: data.default_currency || 'USD',
        website: data.website,
        address: data.address,
        city: data.city,
        postalCode: data.postal_code,
        country: data.country,
        settings: data.settings || {},
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        deletedAt: data.deleted_at,
      };
    },
    onSuccess: (_, variables) => {
      // Invalidate specific company and current company
      queryClient.invalidateQueries({ queryKey: ['company', variables.companyId] });
      queryClient.invalidateQueries({ queryKey: ['company', 'current'] });
    },
  });
}

// =====================================================
// Mutation: Update Company Settings (with JSONB merge)
// =====================================================

/**
 * Update company settings with proper JSONB merge
 * Fetches fresh settings from database before updating to prevent race conditions
 */
export function useUpdateCompanySettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      settings,
    }: {
      companyId: string;
      settings: Partial<Company['settings']>;
    }): Promise<Company> => {
      console.log('[useUpdateCompanySettings] Updating settings:', settings);

      // Fetch current company data to get fresh settings
      const { data: currentCompany, error: fetchError } = await supabase
        .from('companies')
        .select('settings')
        .eq('id', companyId)
        .is('deleted_at', null)
        .single();

      if (fetchError) {
        console.error('[useUpdateCompanySettings] Fetch error:', fetchError);
        throw fetchError;
      }

      // Merge new settings with current settings
      const mergedSettings = {
        ...(currentCompany?.settings || {}),
        ...settings,
      };

      console.log('[useUpdateCompanySettings] Merged settings:', mergedSettings);

      // Update company with merged settings
      const { data: updatedCompany, error: updateError } = await supabase
        .from('companies')
        .update({ settings: mergedSettings })
        .eq('id', companyId)
        .select()
        .single();

      if (updateError) {
        console.error('[useUpdateCompanySettings] Update error:', updateError);
        throw updateError;
      }
      if (!updatedCompany) throw new Error('Failed to update company settings');

      console.log('[useUpdateCompanySettings] Update successful:', updatedCompany.settings);

      return {
        id: updatedCompany.id,
        name: updatedCompany.name,
        logoUrl: updatedCompany.logo_url,
        contactEmail: updatedCompany.contact_email,
        contactPhone: updatedCompany.contact_phone,
        brandColor: updatedCompany.brand_color,
        primaryColor: updatedCompany.primary_color,
        defaultCurrency: updatedCompany.default_currency || 'USD',
        website: updatedCompany.website,
        address: updatedCompany.address,
        city: updatedCompany.city,
        postalCode: updatedCompany.postal_code,
        country: updatedCompany.country,
        settings: updatedCompany.settings || {},
        createdAt: updatedCompany.created_at,
        updatedAt: updatedCompany.updated_at,
        deletedAt: updatedCompany.deleted_at,
      };
    },
    onSuccess: (_, variables) => {
      // Invalidate company queries to refetch with updated settings
      queryClient.invalidateQueries({ queryKey: ['company', variables.companyId] });
      queryClient.invalidateQueries({ queryKey: ['company', 'current'] });
    },
  });
}

// =====================================================
// Mutation: Upload Company Logo
// =====================================================

export function useUploadLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      file,
    }: {
      companyId: string;
      file: File;
    }): Promise<string> => {
      console.log('[useUploadLogo] Starting upload for company:', companyId, 'file:', file.name);

      // Validate file
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        throw new Error('File size must be less than 2MB');
      }

      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('File must be PNG, JPG, or SVG');
      }

      // Delete any existing logo files for this company to prevent orphans
      // List all files in the company's folder and delete them
      const { data: existingFiles, error: listError } = await supabase.storage
        .from('company-logos')
        .list(companyId);

      console.log('[useUploadLogo] Existing files:', existingFiles, 'listError:', listError);

      if (existingFiles && existingFiles.length > 0) {
        const filesToDelete = existingFiles.map(f => `${companyId}/${f.name}`);
        const { error: deleteError } = await supabase.storage.from('company-logos').remove(filesToDelete);
        console.log('[useUploadLogo] Deleted old files:', filesToDelete, 'deleteError:', deleteError);
      }

      // Generate file path: {company_id}/logo.{ext}
      // Sanitize filename to handle special characters in extension
      const sanitizedName = sanitizeFilename(file.name);
      const ext = sanitizedName.split('.').pop() || 'png';
      const filePath = `${companyId}/logo.${ext}`;

      console.log('[useUploadLogo] Uploading to path:', filePath);

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('company-logos')
        .upload(filePath, file, {
          upsert: true, // Replace if exists
          contentType: file.type,
          cacheControl: '604800', // 7 days - enables CDN caching
        });

      console.log('[useUploadLogo] Upload result:', uploadData, 'uploadError:', uploadError);

      if (uploadError) throw uploadError;

      // Get public URL with cache-busting timestamp
      const {
        data: { publicUrl },
      } = supabase.storage.from('company-logos').getPublicUrl(filePath);

      // Add cache-busting timestamp to force browser to fetch new image
      const cacheBustedUrl = `${publicUrl}?t=${Date.now()}`;

      console.log('[useUploadLogo] Public URL:', cacheBustedUrl);

      // Update company logo_url
      const { data: updatedCompany, error: updateError } = await supabase
        .from('companies')
        .update({ logo_url: cacheBustedUrl })
        .eq('id', companyId)
        .select()
        .single();

      console.log('[useUploadLogo] Company update result:', updatedCompany, 'updateError:', updateError);

      if (updateError) throw updateError;
      if (!updatedCompany) throw new Error('Failed to update company logo - check permissions');

      console.log('[useUploadLogo] Success! Updated logo_url:', updatedCompany.logo_url);

      return cacheBustedUrl;
    },
    onSuccess: (_, variables) => {
      // Invalidate company queries
      queryClient.invalidateQueries({ queryKey: ['company', variables.companyId] });
      queryClient.invalidateQueries({ queryKey: ['company', 'current'] });
    },
  });
}

// =====================================================
// Mutation: Delete Company Logo
// =====================================================

export function useDeleteLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (companyId: string): Promise<void> => {
      console.log('[useDeleteLogo] Deleting logo for company:', companyId);

      // Delete all logo files from storage for this company
      const { data: existingFiles, error: listError } = await supabase.storage
        .from('company-logos')
        .list(companyId);

      console.log('[useDeleteLogo] Existing files:', existingFiles, 'listError:', listError);

      if (existingFiles && existingFiles.length > 0) {
        const filesToDelete = existingFiles.map(f => `${companyId}/${f.name}`);
        const { error: deleteError } = await supabase.storage.from('company-logos').remove(filesToDelete);
        console.log('[useDeleteLogo] Deleted files:', filesToDelete, 'deleteError:', deleteError);
      }

      // Update company to remove logo_url
      const { data, error } = await supabase
        .from('companies')
        .update({ logo_url: null })
        .eq('id', companyId)
        .select()
        .single();

      console.log('[useDeleteLogo] Company update result:', data, 'error:', error);

      if (error) throw error;
      if (!data) throw new Error('Failed to remove company logo - check permissions');
    },
    onSuccess: (_, companyId) => {
      queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      queryClient.invalidateQueries({ queryKey: ['company', 'current'] });
    },
  });
}
